import type { CoreStoreType } from '../../store/CoreStore';
import { debounce } from '../../utils/debounce';

import type { ICrossOriginCommunicator, OutgoingInfo } from './types';
import { OutgoingMessage } from './types';

class CrossOriginCommunicationController {
  private readonly handleIncomingMessage = this.processIncomingMessage.bind(this);

  private communicators: ICrossOriginCommunicator[];
  private store: CoreStoreType;

  private debouncedDispatchers: Map<OutgoingMessage, (info?: OutgoingInfo) => void> = new Map();

  constructor() {
    this.communicators = [];
    this.store = {} as CoreStoreType;
  }

  close(): void {
    window.removeEventListener('message', this.handleIncomingMessage);
    this.communicators.forEach((communicator) => communicator.cleanup?.());
  }

  connect(): void {
    window.addEventListener('message', this.handleIncomingMessage);
    this.communicators.forEach((communicator) => communicator.onConnect?.(this.store));
  }

  dispatch(message: OutgoingMessage, info?: OutgoingInfo): void {
    this.communicators.forEach((communicator) => communicator.send(message, info));
  }

  setCommunicator(communicator: ICrossOriginCommunicator): void {
    this.communicators.push(communicator);
  }

  setCommunicators(communicators: ICrossOriginCommunicator[]): void {
    this.communicators = communicators;
  }

  setStore(store: CoreStoreType): void {
    this.store = store;
  }

  dispatchWithDebounce(message: OutgoingMessage, info?: OutgoingInfo, delay = 300): void {
    if (!this.debouncedDispatchers.has(message)) {
      const debounced = debounce((info?: OutgoingInfo) => {
        this.dispatch(message, info);
      }, delay);
      this.debouncedDispatchers.set(message, debounced);
    }

    this.debouncedDispatchers.get(message)?.(info);
  }

  private processIncomingMessage(event: MessageEvent): void {
    this.communicators.forEach((communicator) => communicator.process(event, this.store));
  }
}

export const CrossOriginCommunication = new CrossOriginCommunicationController();
